import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getUserAccountList,
  freezeAccount,
  unfreezeAccount,
  getAccompanyShoppingProviderAccounts,
  getAccompanyShoppingProviderAccountDetail,
  updateAccompanyShoppingProviderServiceFeeRate,
  updateAccompanyShoppingProviderAccount,
  getStoreOwnerAccounts,
  getStoreOwnerAccountDetail,
  addStoreOwnerAccount,
  updateStoreOwnerAccount,
  deleteStoreOwnerAccount,
  deleteStoreOwnerAccountBatch,
  countStoreOwnerAccountsByStatus
} from '../../api/userAccount';
import { message } from 'antd';

// 获取主账户列表的异步 thunk
export const fetchUserAccounts = createAsyncThunk(
  'userAccount/fetchMainAccounts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getUserAccountList(params);
      return response.data;
    } catch (error) {
      message.error('获取主账户列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取陪逛员账户列表的异步 thunk
export const fetchAccompanyShoppingProviderAccounts = createAsyncThunk(
  'userAccount/fetchAccompanyAccounts',
  async (params = {}, { rejectWithValue }) => {
    try {
      // 转换状态枚举值
      const transformedParams = { ...params };
      if (params.status) {
        transformedParams.status = params.status === 'DISABLED' ? 0 : 1;
      }

      const response = await getAccompanyShoppingProviderAccounts(transformedParams);
      return response.data;
    } catch (error) {
      message.error('获取陪逛员账户列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取陪逛员账户详情的异步 thunk
export const fetchAccompanyShoppingProviderAccountDetail = createAsyncThunk(
  'userAccount/fetchAccompanyAccountDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAccompanyShoppingProviderAccountDetail(id);
      return response.data;
    } catch (error) {
      message.error('获取陪逛员账户详情失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 更新陪逛员服务费率的异步 thunk
export const updateAccompanyShoppingProviderServiceFeeRateAction = createAsyncThunk(
  'userAccount/updateAccompanyServiceFeeRate',
  async ({ accountId, serviceFeeRate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingProviderServiceFeeRate(accountId, serviceFeeRate);
      
      if (response.code === 200) {
        message.success('服务费率更新成功');
        // 重新获取陪逛员账户列表
        dispatch(fetchAccompanyShoppingProviderAccounts());
        return response.data;
      } else {
        message.error(response.message || '服务费率更新失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('服务费率更新请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 更新陪逛员账户信息的异步 thunk
export const updateAccompanyShoppingProviderAccountAction = createAsyncThunk(
  'userAccount/updateAccompanyAccount',
  async (accountData, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingProviderAccount(accountData);
      
      if (response.code === 200) {
        message.success('账户信息更新成功');
        // 重新获取陪逛员账户列表
        dispatch(fetchAccompanyShoppingProviderAccounts());
        return response.data;
      } else {
        message.error(response.message || '账户信息更新失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('账户信息更新请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 冻结陪逛员账户的异步 thunk
export const freezeAccompanyShoppingProviderAccountAction = createAsyncThunk(
  'userAccount/freezeAccompanyAccount',
  async (account, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingProviderAccount(account);

      if (response.code === 200) {
        message.success('陪逛员账户冻结成功');
        // 重新获取陪逛员账户列表
        dispatch(fetchAccompanyShoppingProviderAccounts());
        return response.data;
      } else {
        message.error(response.message || '冻结失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('冻结陪逛员账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 解冻陪逛员账户的异步 thunk
export const unfreezeAccompanyShoppingProviderAccountAction = createAsyncThunk(
  'userAccount/unfreezeAccompanyAccount',
  async (account, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingProviderAccount(account);

      if (response.code === 200) {
        message.success('陪逛员账户解冻成功');
        // 重新获取陪逛员账户列表
        dispatch(fetchAccompanyShoppingProviderAccounts());
        return response.data;
      } else {
        message.error(response.message || '解冻失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('解冻陪逛员账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取商家账户列表的异步 thunk
export const fetchStoreOwnerAccounts = createAsyncThunk(
  'userAccount/fetchStoreOwnerAccounts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getStoreOwnerAccounts(params);
      return response.data;
    } catch (error) {
      message.error('获取商家账户列表失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 获取商家账户详情的异步 thunk
export const fetchStoreOwnerAccountDetail = createAsyncThunk(
  'userAccount/fetchStoreOwnerAccountDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getStoreOwnerAccountDetail(id);
      return response.data;
    } catch (error) {
      message.error('获取商家账户详情失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 新增商家账户的异步 thunk
export const addStoreOwnerAccountAction = createAsyncThunk(
  'userAccount/addStoreOwnerAccount',
  async (accountData, { dispatch, rejectWithValue }) => {
    try {
      const response = await addStoreOwnerAccount(accountData);
      
      if (response.code === 200) {
        message.success('商家账户新增成功');
        // 重新获取商家账户列表
        dispatch(fetchStoreOwnerAccounts());
        return response.data;
      } else {
        message.error(response.message || '新增失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('新增商家账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 更新商家账户的异步 thunk
export const updateStoreOwnerAccountAction = createAsyncThunk(
  'userAccount/updateStoreOwnerAccount',
  async (updateData, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateStoreOwnerAccount(updateData);
      
      if (response.code === 200) {
        message.success('商家账户更新成功');
        // 重新获取商家账户列表
        dispatch(fetchStoreOwnerAccounts());
        return response.data;
      } else {
        message.error(response.message || '更新失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('更新商家账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 删除商家账户的异步 thunk
export const deleteStoreOwnerAccountAction = createAsyncThunk(
  'userAccount/deleteStoreOwnerAccount',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteStoreOwnerAccount(id);
      
      if (response.code === 200) {
        message.success('商家账户删除成功');
        // 重新获取商家账户列表
        dispatch(fetchStoreOwnerAccounts());
        return response.data;
      } else {
        message.error(response.message || '删除失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('删除商家账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 批量删除商家账户的异步 thunk
export const deleteStoreOwnerAccountBatchAction = createAsyncThunk(
  'userAccount/deleteStoreOwnerAccountBatch',
  async (ids, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteStoreOwnerAccountBatch(ids);
      
      if (response.code === 200) {
        message.success('批量删除商家账户成功');
        // 重新获取商家账户列表
        dispatch(fetchStoreOwnerAccounts());
        return response.data;
      } else {
        message.error(response.message || '批量删除失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('批量删除商家账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 统计商家账户数量的异步 thunk
export const countStoreOwnerAccountsByStatusAction = createAsyncThunk(
  'userAccount/countStoreOwnerAccountsByStatus',
  async (status, { rejectWithValue }) => {
    try {
      const response = await countStoreOwnerAccountsByStatus(status);
      return response.data;
    } catch (error) {
      message.error('统计商家账户数量失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 冻结主账户的异步 thunk
export const freezeAccountAction = createAsyncThunk(
  'userAccount/freeze',
  async ({ userId, accountType }, { dispatch, rejectWithValue }) => {
    try {
      const response = await freezeAccount(userId);

      if (response.code === 200) {
        message.success('账户冻结成功');
        // 根据账户类型重新获取列表
        switch (accountType) {
          case 'MAIN':
            dispatch(fetchUserAccounts());
            break;
          case 'ACCOMPANY':
            dispatch(fetchAccompanyShoppingProviderAccounts());
            break;
          case 'STORE_OWNER':
            dispatch(fetchStoreOwnerAccounts());
            break;
        }
        return response.data;
      } else {
        message.error(response.message || '冻结失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('冻结账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

// 解冻主账户的异步 thunk
export const unfreezeAccountAction = createAsyncThunk(
  'userAccount/unfreeze',
  async ({ userId, accountType }, { dispatch, rejectWithValue }) => {
    try {
      const response = await unfreezeAccount(userId);

      if (response.code === 200) {
        message.success('账户解冻成功');
        // 根据账户类型重新获取列表
        switch (accountType) {
          case 'MAIN':
            dispatch(fetchUserAccounts());
            break;
          case 'ACCOMPANY':
            dispatch(fetchAccompanyShoppingProviderAccounts());
            break;
          case 'STORE_OWNER':
            dispatch(fetchStoreOwnerAccounts());
            break;
        }
        return response.data;
      } else {
        message.error(response.message || '解冻失败');
        return rejectWithValue(response);
      }
    } catch (error) {
      message.error('解冻账户请求失败');
      return rejectWithValue(error.response?.data);
    }
  }
);

const userAccountSlice = createSlice({
  name: 'userAccount',
  initialState: {
    mainAccountList: {
      data: [],
      total: 0,
      current: 1,
      size: 10,
      loading: false
    },
    accompanyAccountList: {
      data: [],
      total: 0,
      current: 1,
      size: 10,
      loading: false
    },
    accompanyAccountDetail: null,
    storeOwnerAccountList: {
      data: [],
      total: 0,
      current: 1,
      size: 10,
      loading: false
    },
    storeOwnerAccountDetail: null,
    storeOwnerAccountCount: null,
    searchParams: {},
    error: null
  },
  reducers: {
    setSearchParams: (state, action) => {
      state.searchParams = action.payload;
    },
    resetSearchParams: (state) => {
      state.searchParams = {};
    }
  },
  extraReducers: (builder) => {
    builder
      // 主账户列表
      .addCase(fetchUserAccounts.pending, (state) => {
        state.mainAccountList.loading = true;
        state.error = null;
      })
      .addCase(fetchUserAccounts.fulfilled, (state, action) => {
        state.mainAccountList.loading = false;
        state.mainAccountList.data = action.payload.records;
        state.mainAccountList.total = action.payload.total;
        state.mainAccountList.current = action.payload.current;
        state.mainAccountList.size = action.payload.size;
      })
      .addCase(fetchUserAccounts.rejected, (state, action) => {
        state.mainAccountList.loading = false;
        state.error = action.payload;
      })
      // 陪逛员账户列表
      .addCase(fetchAccompanyShoppingProviderAccounts.pending, (state) => {
        state.accompanyAccountList.loading = true;
        state.error = null;
      })
      .addCase(fetchAccompanyShoppingProviderAccounts.fulfilled, (state, action) => {
        state.accompanyAccountList.loading = false;
        state.accompanyAccountList.data = action.payload.records;
        state.accompanyAccountList.total = action.payload.total;
        state.accompanyAccountList.current = action.payload.current;
        state.accompanyAccountList.size = action.payload.size;
      })
      .addCase(fetchAccompanyShoppingProviderAccounts.rejected, (state, action) => {
        state.accompanyAccountList.loading = false;
        state.error = action.payload;
      })
      // 陪逛员账户详情
      .addCase(fetchAccompanyShoppingProviderAccountDetail.fulfilled, (state, action) => {
        state.accompanyAccountDetail = action.payload;
      })
      // 商家账户列表
      .addCase(fetchStoreOwnerAccounts.pending, (state) => {
        state.storeOwnerAccountList.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreOwnerAccounts.fulfilled, (state, action) => {
        state.storeOwnerAccountList.loading = false;
        state.storeOwnerAccountList.data = action.payload.records;
        state.storeOwnerAccountList.total = action.payload.total;
        state.storeOwnerAccountList.current = action.payload.current;
        state.storeOwnerAccountList.size = action.payload.size;
      })
      .addCase(fetchStoreOwnerAccounts.rejected, (state, action) => {
        state.storeOwnerAccountList.loading = false;
        state.error = action.payload;
      })
      // 商家账户详情
      .addCase(fetchStoreOwnerAccountDetail.fulfilled, (state, action) => {
        state.storeOwnerAccountDetail = action.payload;
      });
  }
});

export const {
  setSearchParams,
  resetSearchParams
} = userAccountSlice.actions;

export default userAccountSlice.reducer; 